import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from '@/components/ui/sonner';
import { useAuth } from '@/contexts/AuthContext';
import {
  useFamilyGraph,
} from '@/contexts/FamilyGraphContext';
import {
  createCanonicalGenericInviteAcceptanceOperationId,
  executeCanonicalGenericInviteAcceptanceRuntime,
  type CanonicalGenericInviteRelationshipIntent,
} from '@/governor/tree/CanonicalGenericInviteAcceptanceRuntime';
import { supabase } from '@/lib/supabase';
import { normalizePhoneToE164 } from '@/lib/contactIdentity';
import { getInviteContext, markInviteBound } from '@/lib/inviteContext';
import {
  HeartHandshake,
  Loader2,
  Users,
  Baby,
  UserSquare2,
  Crown,
  GitBranch,
  Sparkles,
  Heart,
} from 'lucide-react';


/**
 * Relationship-onboarding modal shown to a brand-new user who signed up via
 * an invite link of the form `/invite/{inviter_user_id}`.
 *
 * It asks: "How are you related to {Inviter Name}?" and, on submit:
 *   1. Creates a family_graph_members row in the inviter's tree describing
 *      the new user (so they appear in the inviter's tree immediately).
 *   2. Mirrors a corresponding row in the new user's own tree for the
 *      inviter so the relationship shows on both sides.
 *   3. Stamps `profiles.invited_by = inviter_user_id` for analytics +
 *      auto-merge later.
 */

interface InviterInfo {
  id: string;
  name: string;
  family_name?: string | null;
}

type RelChoice =
  | 'mother'
  | 'father'
  | 'child'
  | 'sibling'
  | 'spouse'
  | 'cousin'
  | 'aunt-uncle'
  | 'niece-nephew'
  | 'grandparent'
  | 'grandchild'
  | 'other';

const CHOICES: { value: RelChoice; label: string; icon: React.ReactNode; helper: string }[] = [
  { value: 'mother', label: 'Mother', icon: <Crown className="w-4 h-4" />, helper: 'I am their mother' },
  { value: 'father', label: 'Father', icon: <Crown className="w-4 h-4" />, helper: 'I am their father' },
  { value: 'child', label: 'Child', icon: <Baby className="w-4 h-4" />, helper: 'I am their son / daughter' },
  { value: 'sibling', label: 'Sibling', icon: <Users className="w-4 h-4" />, helper: 'We share a parent' },
  { value: 'spouse', label: 'Spouse / Partner', icon: <Heart className="w-4 h-4" />, helper: 'We are married / partners' },
  { value: 'cousin', label: 'Cousin', icon: <GitBranch className="w-4 h-4" />, helper: 'We share a grandparent' },
  { value: 'aunt-uncle', label: 'Aunt / Uncle', icon: <UserSquare2 className="w-4 h-4" />, helper: 'I am their parent\'s sibling' },
  { value: 'niece-nephew', label: 'Niece / Nephew', icon: <Sparkles className="w-4 h-4" />, helper: 'I am their sibling\'s child' },
  { value: 'grandparent', label: 'Grandparent', icon: <Crown className="w-4 h-4" />, helper: 'I am their grandma / grandpa' },
  { value: 'grandchild', label: 'Grandchild', icon: <Baby className="w-4 h-4" />, helper: 'They are my grandparent' },
  { value: 'other', label: 'Other relation', icon: <HeartHandshake className="w-4 h-4" />, helper: 'I\'ll set this up later' },
];

interface Props {
  open: boolean;
  inviter: InviterInfo | null;
  onClose: () => void;
}

interface InviteProfileCandidate {
  memberId: string;
  name: string | null;
  displayName: string | null;
  nickname: string | null;
  relationship: string | null;
  side: string | null;
  birthDate: string | null;
  photoUrl: string | null;
  score: number;
}

type ProfileDecision =
  | 'selected'
  | 'create'
  | null;

interface TargetedInviteContext {
  person_targeted: true;
  invite_id: string;
  invite_type:
    | 'FAMILY_PROFILE'
    | 'SPONSORED_SEAT';
  status: string | null;
  target_member_id: string;
  target_name: string | null;
  recipient_identity_mode:
    | 'CONFIRMED_EMAIL'
    | 'CONFIRMED_PHONE';
  recipient_hint: string | null;
  recipient_verified: boolean;
  phone_conflict: boolean;
  seat_reservation_expires_at:
    | string
    | null;
  expires_at: string | null;
}

const TargetedInviteAcceptance: React.FC<{
  inviter: InviterInfo;
  context: TargetedInviteContext;
  onClose: () => void;
}> = ({
  inviter,
  context,
  onClose,
}) => {
  const { refreshProfile } = useAuth();

  const inviteToken =
    getInviteContext()?.token ?? null;

  const [phone, setPhone] =
    useState('');

  const [normalizedPhone, setNormalizedPhone] =
    useState<string | null>(null);

  const [verificationCode, setVerificationCode] =
    useState('');

  const [verificationSent, setVerificationSent] =
    useState(false);

  const [recipientVerified, setRecipientVerified] =
    useState(context.recipient_verified);

  const [verifying, setVerifying] =
    useState(false);

  const [submittingTargeted, setSubmittingTargeted] =
    useState(false);

  useEffect(() => {
    setRecipientVerified(
      context.recipient_verified,
    );
  }, [context.recipient_verified]);

  const sendPhoneVerification = async () => {
    if (!inviteToken) {
      toast.error(
        'This invitation is missing its secure token.',
      );
      return;
    }

    const normalized =
      normalizePhoneToE164(phone);

    if (!normalized.valid) {
      toast.error(
        'Enter the mobile number that received this invitation.',
      );
      return;
    }

    setVerifying(true);

    try {
      const {
        data: preflight,
        error: preflightError,
      } = await supabase.rpc(
        'preflight_person_targeted_invite_phone',
        {
          p_invite_token: inviteToken,
          p_phone:
            normalized.identity.e164,
        },
      );

      if (preflightError) {
        throw preflightError;
      }

      setNormalizedPhone(
        normalized.identity.e164,
      );

      if (preflight?.recipient_verified) {
        setRecipientVerified(true);
        toast.success(
          'Your phone is already verified.',
        );
        return;
      }

      const { error } =
        await supabase.auth.updateUser({
          phone: normalized.identity.e164,
        });

      if (error) {
        throw error;
      }

      setVerificationSent(true);

      toast.success(
        'Verification code sent.',
        {
          description:
            'Enter the code to prove control of the phone that received this invitation.',
        },
      );
    } catch (error: any) {
      toast.error(
        error?.message ||
          'KinsCircle could not send the phone verification code.',
      );
    } finally {
      setVerifying(false);
    }
  };

  const verifyPhoneCode = async () => {
    if (
      !normalizedPhone ||
      !verificationCode.trim()
    ) {
      toast.error(
        'Enter the verification code.',
      );
      return;
    }

    setVerifying(true);

    try {
      const { error } =
        await supabase.auth.verifyOtp({
          phone: normalizedPhone,
          token: verificationCode.trim(),
          type: 'phone_change',
        });

      if (error) {
        throw error;
      }

      setRecipientVerified(true);
      setVerificationSent(false);

      toast.success(
        'Phone verified.',
      );
    } catch (error: any) {
      toast.error(
        error?.message ||
          'The phone verification code was not accepted.',
      );
    } finally {
      setVerifying(false);
    }
  };

  const acceptTargetedInvite = async () => {
    if (!inviteToken) {
      toast.error(
        'This invitation is missing its secure token.',
      );
      return;
    }

    if (!recipientVerified) {
      toast.error(
        'Verify the invited recipient identity first.',
      );
      return;
    }

    const acceptanceMode =
      getInviteContext()?.acceptanceMode ??
      null;

    const explicitlyDeclinedSponsoredSeat =
      acceptanceMode === 'SELF_PAID' ||
      acceptanceMode ===
        'FAMILY_CONTRIBUTOR';

    const effectiveAcceptanceMode =
      context.invite_type ===
        'SPONSORED_SEAT' &&
      !explicitlyDeclinedSponsoredSeat
        ? 'SPONSORED_SEAT'
        : 'FAMILY_PROFILE';

    setSubmittingTargeted(true);

    try {
      const {
        data,
        error,
      } = await supabase.rpc(
        'accept_invite_and_join_tree_guided',
        {
          invite_token: inviteToken,
          acceptance_mode:
            effectiveAcceptanceMode,
          selected_member_id: null,
          member_name: null,
          relationship_choice: 'other',
        },
      );

      if (error) {
        throw error;
      }

      if (
        !data ||
        typeof data.member_id !== 'string' ||
        data.member_id !==
          context.target_member_id
      ) {
        throw new Error(
          'TARGETED_ACCEPTANCE_COMMIT_MISMATCH',
        );
      }

      await refreshProfile();

      try {
        window.dispatchEvent(
          new CustomEvent(
            'kc:family-graph-refresh',
          ),
        );
      } catch {
        /* non-fatal */
      }

      try {
        markInviteBound(inviter.id);
      } catch {
        /* non-fatal */
      }

      toast.success(
        `Welcome to ${inviter.name.split(' ')[0]}'s family!`,
        {
          description:
            context.invite_type ===
              'SPONSORED_SEAT' &&
            effectiveAcceptanceMode ===
              'SPONSORED_SEAT'
              ? 'Your exact family place and sponsored membership seat are now connected.'
              : 'Your account is connected to your exact family placecard.',
        },
      );

      onClose();
    } catch (error: any) {
      toast.error(
        error?.message ||
          'KinsCircle could not complete this person-specific invitation.',
      );
    } finally {
      setSubmittingTargeted(false);
    }
  };

  const targetName =
    context.target_name ||
    'your family place';

  return (
    <Dialog
      open
      onOpenChange={(nextOpen) => {
        if (!nextOpen) onClose();
      }}
    >
      <DialogContent className="max-w-md border-violet-200 bg-gradient-to-br from-violet-50 to-fuchsia-50">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl text-violet-950">
            Claim your place in the family
          </DialogTitle>
          <DialogDescription className="text-violet-900/70">
            {inviter.name} invited you to the existing placecard for {targetName}. KinsCircle will not ask you to choose another family identity.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="rounded-xl border border-violet-200 bg-white p-3">
            <div className="text-xs font-semibold uppercase tracking-wider text-violet-800">
              Exact family placecard
            </div>
            <div className="mt-1 font-bold text-slate-950">
              {targetName}
            </div>
            <div className="mt-1 text-xs text-slate-600">
              Recipient proof: {context.recipient_hint || 'private'}
            </div>
          </div>

          {context.phone_conflict && (
            <div className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-900">
              This signed-in account already owns a different confirmed phone number. KinsCircle will not replace it automatically. Sign in with the intended account before claiming this placecard.
            </div>
          )}

          {context.recipient_identity_mode ===
            'CONFIRMED_EMAIL' &&
            !recipientVerified && (
              <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
                Sign in with the confirmed email address that received this invitation ({context.recipient_hint || 'private email'}).
              </div>
            )}

          {context.recipient_identity_mode ===
            'CONFIRMED_PHONE' &&
            !recipientVerified &&
            !context.phone_conflict && (
              <div className="space-y-3 rounded-xl border border-blue-200 bg-blue-50 p-3">
                <div className="text-sm font-semibold text-blue-950">
                  Verify the phone that received this invitation
                </div>

                <Input
                  type="tel"
                  value={phone}
                  onChange={(event) =>
                    setPhone(
                      event.target.value,
                    )
                  }
                  placeholder="+1 601 555 0123"
                  className="bg-white"
                />

                {!verificationSent ? (
                  <Button
                    type="button"
                    onClick={() =>
                      void sendPhoneVerification()
                    }
                    disabled={verifying}
                    className="w-full"
                  >
                    {verifying && (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    )}
                    Send verification code
                  </Button>
                ) : (
                  <>
                    <Input
                      inputMode="numeric"
                      value={verificationCode}
                      onChange={(event) =>
                        setVerificationCode(
                          event.target.value,
                        )
                      }
                      placeholder="Verification code"
                      className="bg-white"
                    />
                    <Button
                      type="button"
                      onClick={() =>
                        void verifyPhoneCode()
                      }
                      disabled={
                        verifying ||
                        !verificationCode.trim()
                      }
                      className="w-full"
                    >
                      {verifying && (
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      )}
                      Verify phone
                    </Button>
                  </>
                )}
              </div>
            )}

          {recipientVerified && (
            <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-900">
              Recipient identity verified. KinsCircle can now bind this account only to the exact placecard above.
            </div>
          )}

          <Button
            type="button"
            onClick={() =>
              void acceptTargetedInvite()
            }
            disabled={
              submittingTargeted ||
              !recipientVerified ||
              context.phone_conflict
            }
            className="w-full bg-gradient-to-r from-violet-700 to-fuchsia-600 text-white"
          >
            {submittingTargeted && (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            )}
            {context.invite_type ===
              'SPONSORED_SEAT'
              ? 'Claim my place and membership seat'
              : 'Claim my family place'}
          </Button>

          <button
            type="button"
            onClick={onClose}
            className="w-full text-xs text-violet-700/70 hover:underline"
          >
            Finish this later
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};


const RelationshipOnboardingModal: React.FC<Props> = ({ open, inviter, onClose }) => {
  const { user, memberName, refreshProfile } = useAuth();

  const {
    members,
    effectiveOwnerUserId,
  } = useFamilyGraph();

  const [choice, setChoice] =
    useState<RelChoice>('other');

  const [yourName, setYourName] =
    useState(memberName || '');

  const [
    candidates,
    setCandidates,
  ] = useState<InviteProfileCandidate[]>([]);

  const [
    selectedMemberId,
    setSelectedMemberId,
  ] = useState<string | null>(null);

  const [
    profileDecision,
    setProfileDecision,
  ] = useState<ProfileDecision>(null);

  const [
    searchingProfiles,
    setSearchingProfiles,
  ] = useState(false);

  const [
    profileSearchComplete,
    setProfileSearchComplete,
  ] = useState(false);

  const [submitting, setSubmitting] =
    useState(false);

  const [
    targetedInviteContext,
    setTargetedInviteContext,
  ] =
    useState<TargetedInviteContext | null>(null);

  const [
    inviteContextResolved,
    setInviteContextResolved,
  ] = useState(false);

  useEffect(() => {
    if (!open) return;

    setChoice('other');
    setYourName(memberName || '');
    setCandidates([]);
    setSelectedMemberId(null);
    setProfileDecision(null);
    setSearchingProfiles(false);
    setProfileSearchComplete(false);
  }, [open, memberName]);

  useEffect(() => {
    if (!open) {
      setTargetedInviteContext(null);
      setInviteContextResolved(false);
      return;
    }

    const inviteToken =
      getInviteContext()?.token ?? null;

    if (!inviteToken) {
      setTargetedInviteContext(null);
      setInviteContextResolved(true);
      return;
    }

    let cancelled = false;

    const resolveTargetedContext =
      async () => {
        try {
          const { data, error } =
            await supabase.rpc(
              'get_person_targeted_invite_context',
              {
                p_invite_token:
                  inviteToken,
              },
            );

          if (cancelled) return;

          if (error) {
            throw error;
          }

          setTargetedInviteContext(
            data?.person_targeted === true
              ? (
                  data as
                    TargetedInviteContext
                )
              : null,
          );
        } catch {
          if (!cancelled) {
            setTargetedInviteContext(null);
          }
        } finally {
          if (!cancelled) {
            setInviteContextResolved(true);
          }
        }
      };

    void resolveTargetedContext();

    return () => {
      cancelled = true;
    };
  }, [open]);

  if (!inviter) return null;

  if (!inviteContextResolved) {
    return (
      <Dialog open>
        <DialogContent className="max-w-md">
          <div className="flex items-center justify-center gap-2 py-8 text-sm text-slate-600">
            <Loader2 className="h-4 w-4 animate-spin" />
            Verifying invitation authority…
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  if (targetedInviteContext) {
    return (
      <TargetedInviteAcceptance
        inviter={inviter}
        context={targetedInviteContext}
        onClose={onClose}
      />
    );
  }
  const findLikelyProfiles =
    async () => {
      const inviteContext =
        getInviteContext();

      const inviteToken =
        inviteContext?.token ?? null;

      if (!inviteToken) {
        toast.error(
          'This invitation is missing its secure token.',
        );
        return;
      }

      if (!yourName.trim()) {
        toast.error(
          'Enter your name or nickname first.',
        );
        return;
      }

      setSearchingProfiles(true);
      setProfileSearchComplete(false);
      setCandidates([]);
      setSelectedMemberId(null);
      setProfileDecision(null);

      try {
        const { data, error } =
          await supabase.rpc(
            'get_invite_profile_candidates',
            {
              p_invite_token:
                inviteToken,
              p_name:
                yourName.trim(),
              p_birth_date: null,
              p_relationship:
                choice === 'other'
                  ? null
                  : choice,
            },
          );

        if (error) {
          throw error;
        }

        const candidateRows =
          data &&
          typeof data === 'object' &&
          Array.isArray(
            data.candidates,
          )
            ? data.candidates
            : [];

        setCandidates(
          candidateRows as
            InviteProfileCandidate[],
        );

        setProfileSearchComplete(true);

        if (
          candidateRows.length === 0
        ) {
          setProfileDecision('create');
        }
      } catch (candidateError: any) {
        console.error(
          '[RelOnboard] candidate search failed',
          candidateError,
        );

        toast.error(
          candidateError?.message ||
            'KinsCircle could not search the family profiles.',
        );
      } finally {
        setSearchingProfiles(false);
      }
    };

  const selectCandidate = (
    candidate: InviteProfileCandidate,
  ) => {
    const candidateName =
      candidate.name ||
      candidate.displayName ||
      candidate.nickname ||
      'this family profile';

    setSelectedMemberId(null);
    setProfileDecision(null);

    toast.message(
      `${candidateName} may already be your placecard.`,
      {
        description:
          'For identity safety, a general invite cannot claim an existing placecard. Ask the Tree owner to open that placecard and send you a direct invitation.',
      },
    );
  };

  const chooseCreateProfile = () => {
    setSelectedMemberId(null);
    setProfileDecision('create');
  };

  const resetProfileSearch = () => {
    setCandidates([]);
    setSelectedMemberId(null);
    setProfileDecision(null);
    setProfileSearchComplete(false);
  };

  const submit = async () => {
    if (
      profileDecision !==
        'create'
    ) {
      toast.error(
        'Existing family placecards require a direct person-specific invitation.',
      );
      return;
    }

    if (!yourName.trim()) {
      toast.error(
        'Enter your name to create your profile.',
      );
      return;
    }

    if (!user?.id) {
      toast.error(
        'Please finish signing up first',
      );
      return;
    }

    const inviteContext =
      getInviteContext();

    const inviteToken =
      inviteContext?.token ??
      null;

    if (!inviteToken) {
      toast.error(
        'This invitation is missing its secure token. Ask the sender to create a new invitation.',
      );
      return;
    }

    if (
      effectiveOwnerUserId !==
        inviter.id ||
      members.length ===
        0
    ) {
      toast.error(
        'KinsCircle has not loaded the exact inviter family graph yet. Reopen this invitation and try again.',
      );
      return;
    }

    const myName =
      (
        yourName ||
        memberName ||
        user.email?.split('@')[0] ||
        'New family member'
      ).trim();

    const operationId =
      createCanonicalGenericInviteAcceptanceOperationId();

    setSubmitting(true);

    try {
      const acceptanceResult =
        await executeCanonicalGenericInviteAcceptanceRuntime({
          supabase,

          members,

          inviterUserId:
            inviter.id,

          claimantUserId:
            user.id,

          inviteToken,

          acceptanceMode:
            inviteContext
              ?.acceptanceMode ??
            null,

          relationshipIntent:
            choice as
              CanonicalGenericInviteRelationshipIntent,

          createNewName:
            myName,

          similarityWarningMemberIds:
            candidates.map(
              candidate =>
                candidate.memberId,
            ),

          operationId,
        });

      if (acceptanceResult.ok === false) {
        throw new Error(
          acceptanceResult.message,
        );
      }

      console.debug(
        '[RelOnboard] canonical generic invite acceptance completed',
        {
          userId:
            user.id,

          inviterUserId:
            inviter.id,

          viewerMemberId:
            acceptanceResult
              .subjectMemberId,

          relationshipChoice:
            choice,

          operationId:
            acceptanceResult
              .operationId,

          acceptanceResult,
        },
      );

      await refreshProfile();

      try {
        window.dispatchEvent(
          new CustomEvent(
            'kc:family-graph-refresh',
          ),
        );
      } catch {
        /* non-fatal */
      }

      try {
        markInviteBound(
          inviter.id,
        );
      } catch {
        /* non-fatal */
      }

      const acceptanceMode =
        inviteContext
          ?.acceptanceMode ??
        null;

      toast.success(
        `Welcome to ${
          inviter.name.split(' ')[0]
        }'s family!`,
        {
          description:
            acceptanceMode ===
              'SPONSORED_SEAT'
              ? 'Your sponsored membership seat is active.'
              : acceptanceMode ===
                  'SELF_PAID'
                ? 'You are connected. Complete your membership purchase to unlock paid features.'
                : 'You joined as a Family Contributor.',
        },
      );

      onClose();
    } catch (err: any) {
      toast.error(
        err?.message ||
          'Could not link your account',
        {
          description:
            'No partial family-tree connection was committed. Existing placecards require a direct person-specific invitation.',
        },
      );
    } finally {
      setSubmitting(false);
    }
  };



  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) onClose();
      }}
    >
      <DialogContent className="max-h-[90vh] max-w-md overflow-y-auto border-fuchsia-200 bg-gradient-to-br from-fuchsia-50 to-pink-50">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-serif text-2xl text-fuchsia-900">
            <HeartHandshake className="h-6 w-6 text-fuchsia-700" />
            Join {inviter.name.split(' ')[0]}'s family
          </DialogTitle>

          <DialogDescription className="text-fuchsia-900/70">
            KinsCircle may show possible existing placecards. Matches are advisory only; an existing placecard must be invited directly by the Tree owner.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5">
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-fuchsia-900">
              Your name or nickname
            </label>

            <Input
              value={yourName}
              onChange={(event) => {
                setYourName(
                  event.target.value,
                );
                resetProfileSearch();
              }}
              placeholder="Example: Melissa Coleman or Missy"
              className="mt-1 border-fuchsia-200 bg-white"
            />

            <p className="mt-1 text-xs text-slate-600">
              The spelling does not have to match exactly.
            </p>
          </div>

          <Button
            type="button"
            variant="outline"
            onClick={() =>
              void findLikelyProfiles()
            }
            disabled={
              searchingProfiles ||
              !yourName.trim()
            }
            className="w-full border-fuchsia-300 bg-white text-fuchsia-800"
          >
            {searchingProfiles ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Users className="mr-2 h-4 w-4" />
            )}

            {searchingProfiles
              ? 'Searching family profiles…'
              : 'Find my family profile'}
          </Button>

          {profileSearchComplete &&
            candidates.length > 0 && (
              <div>
                <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-fuchsia-900">
                  Could one of these be you?
                </div>

                <div className="space-y-2">
                  {candidates.map(
                    (candidate) => {
                      const selected =
                        selectedMemberId ===
                        candidate.memberId;

                      const candidateName =
                        candidate.name ||
                        candidate.displayName ||
                        candidate.nickname ||
                        'Family profile';

                      return (
                        <button
                          key={
                            candidate.memberId
                          }
                          type="button"
                          onClick={() =>
                            selectCandidate(
                              candidate,
                            )
                          }
                          className={`w-full rounded-xl border-2 p-3 text-left transition ${
                            selected
                              ? 'border-fuchsia-500 bg-white shadow-md'
                              : 'border-fuchsia-100 bg-white/80 hover:border-fuchsia-300'
                          }`}
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div>
                              <div className="font-bold text-slate-900">
                                {
                                  candidateName
                                }
                              </div>

                              {candidate.nickname &&
                                candidate.nickname !==
                                  candidateName && (
                                  <div className="text-xs text-slate-600">
                                    Also known as{' '}
                                    {
                                      candidate.nickname
                                    }
                                  </div>
                                )}

                              <div className="mt-1 text-xs text-slate-500">
                                {candidate.relationship ||
                                  'Relationship not yet confirmed'}
                              </div>
                            </div>

                            <span className="rounded-full bg-fuchsia-100 px-2 py-1 text-[11px] font-semibold text-fuchsia-800">
                              Needs direct invite
                            </span>
                          </div>
                        </button>
                      );
                    },
                  )}
                </div>
              </div>
            )}

          {profileSearchComplete && (
            <button
              type="button"
              onClick={
                chooseCreateProfile
              }
              className={`w-full rounded-xl border-2 p-3 text-left transition ${
                profileDecision ===
                'create'
                  ? 'border-amber-500 bg-amber-50'
                  : 'border-slate-200 bg-white hover:border-amber-300'
              }`}
            >
              <div className="font-bold text-slate-900">
                None of these are me
              </div>

              <div className="text-xs text-slate-600">
                Create a new family profile using the name
                above.
              </div>
            </button>
          )}

          {profileDecision && (
            <div>
              <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-fuchsia-900">
                Confirm your relationship
              </div>

              <div className="grid grid-cols-2 gap-2">
                {CHOICES.map(
                  (relationship) => (
                    <button
                      key={
                        relationship.value
                      }
                      type="button"
                      onClick={() =>
                        setChoice(
                          relationship.value,
                        )
                      }
                      className={`rounded-xl border-2 px-3 py-2 text-left transition ${
                        choice ===
                        relationship.value
                          ? 'border-fuchsia-500 bg-white shadow-md'
                          : 'border-fuchsia-100 bg-white/70 hover:border-fuchsia-300'
                      }`}
                    >
                      <div className="flex items-center gap-1.5 text-sm font-bold text-fuchsia-700">
                        {
                          relationship.icon
                        }
                        {
                          relationship.label
                        }
                      </div>

                      <div className="mt-0.5 text-[11px] text-slate-600">
                        {
                          relationship.helper
                        }
                      </div>
                    </button>
                  ),
                )}
              </div>
            </div>
          )}

          <Button
            type="button"
            onClick={() =>
              void submit()
            }
            disabled={
              submitting ||
              !profileDecision ||
              (
                profileDecision ===
                  'create' &&
                !yourName.trim()
              )
            }
            className="w-full gap-2 bg-gradient-to-r from-fuchsia-600 via-pink-500 to-amber-400 font-semibold text-white shadow-md"
          >
            {submitting ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Sparkles className="h-4 w-4" />
            )}

            {submitting
              ? 'Connecting your family…'
              : profileDecision ===
                'selected'
                ? 'Claim this profile and join'
                : 'Create my profile and join'}
          </Button>

          <button
            type="button"
            onClick={onClose}
            className="w-full text-xs text-fuchsia-700/70 underline-offset-2 hover:text-fuchsia-900 hover:underline"
          >
            Finish this later
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default RelationshipOnboardingModal;
